// DoctorQueuePanel — one doctor's live queue on a clean white card.
//
// Bookings are grouped into the doctor's sessions (availabilities). Only ONE
// session is shown at a time, with a switcher to move between them. Each
// patient is a single line: token · name · current-queue tag.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Doctor } from "../../lib/utils/types";
import { formatDoctorName } from "../../lib/utils/formatDoctorName";
import { BookingV2, DocDelay, SessionMeta } from "../types";
import { useDisplayName } from "../utils/displayName";
import { useTVConfig } from "../contexts/TVConfigContext";
import { getRoomMapping, RoomMapping } from "../api/room";
import PatientRow from "./PatientRow";
import RoomBadge from "./RoomBadge";

const byToken = (a: BookingV2, b: BookingV2) =>
  (a.token_number ?? 0) - (b.token_number ?? 0);

const TIME_FORMATS = [moment.ISO_8601, "HH:mm:ss", "HH:mm", "h:mm A"] as const;

const fmtTime = (t?: string): string | null => {
  if (!t) return null;
  const m = moment(t, TIME_FORMATS as unknown as string[], true);
  return m.isValid() ? m.format("h:mm A") : null;
};

const timeValue = (t?: string): number => {
  if (!t) return Number.MAX_SAFE_INTEGER;
  const m = moment(t, TIME_FORMATS as unknown as string[]);
  return m.isValid() ? m.valueOf() : Number.MAX_SAFE_INTEGER;
};

const rangeLabel = (meta: SessionMeta | undefined, index: number): string => {
  const start = fmtTime(meta?.start_time);
  const end = fmtTime(meta?.end_time);
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  return `Session ${index + 1}`;
};

const initialsOf = (name?: string): string => {
  if (!name) return "Dr";
  const parts = name.replace(/^dr\.?\s*/i, "").trim().split(/\s+/);
  return (parts[0]?.[0] ?? "").concat(parts[1]?.[0] ?? "").toUpperCase() || "Dr";
};

// `size` is in px at the 1080p design size; rendered in rem so it follows the
// board's viewport scaling (useTVScale) like the rest of the panel.
const Avatar: React.FC<{ doctor: Doctor; size: number }> = ({ doctor, size }) => {
  const style = { width: `${size / 16}rem`, height: `${size / 16}rem` };
  if (doctor.profile_picture) {
    return (
      <img
        src={doctor.profile_picture}
        alt={doctor.full_name}
        className="rounded-full object-cover ring-1 ring-[#E5E9F0]"
        style={style}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-[#EAF2FF] text-[#0B74FF] font-semibold flex items-center justify-center ring-1 ring-[#D7E6FF]"
      style={{ ...style, fontSize: `${(size * 0.38) / 16}rem` }}
    >
      {initialsOf(doctor.full_name)}
    </div>
  );
};

interface SessionGroup {
  availabilityId: string;
  meta?: SessionMeta;
  bookings: BookingV2[]; // all visible patients in the session, token-sorted
}

// How much room a panel has: "large" = slideshow (one doctor per screen),
// "normal" = a column, "compact" = a column sharing the board with the ads
// panel (a quarter of the screen). Decided by RotatingBoard.
export type Density = "compact" | "normal" | "large";

interface Props {
  doctor: Doctor;
  bookings: BookingV2[];
  delaysByAvailability: Record<string, DocDelay>;
  sessionsByAvailability: Record<string, SessionMeta>;
  density?: Density;
}

const DoctorQueuePanel: React.FC<Props> = ({
  doctor,
  bookings,
  delaysByAvailability,
  sessionsByAvailability,
  density = "normal",
}) => {
  const formatName = useDisplayName();
  const tvConfig = useTVConfig();
  const large = density === "large";
  const compact = density === "compact";

  // Rooms assigned to this doctor's mapping. Fetched unconditionally (like v1)
  // so the request always fires; the room toggle only governs *display*. Same
  // rule as v1: with the toggle on, a single room shows one doctor-level badge
  // (all patients go there); multiple rooms means each patient carries their
  // own room, shown as a per-row tag.
  const [rooms, setRooms] = useState<RoomMapping[]>([]);
  useEffect(() => {
    if (!doctor.mapping_id) {
      setRooms([]);
      return;
    }
    let cancelled = false;
    getRoomMapping(doctor.mapping_id)
      .then((r) => {
        if (!cancelled) setRooms(r);
      })
      .catch((err) => {
        console.warn("[queue-v2] room mapping load failed:", err);
        if (!cancelled) setRooms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [doctor.mapping_id]);

  const doctorRoom = tvConfig.room_number && rooms.length === 1 ? rooms[0].room_name : null;
  const perPatientRoom = tvConfig.room_number && rooms.length > 1;

  // This doctor's own session ids. A booking whose availability_id is NOT one
  // of them is transferred IN from another doctor (its token/slot live in the
  // origin doctor's queue).
  const mySessionIds = useMemo(
    () =>
      new Set(
        Object.values(sessionsByAvailability)
          .filter((s) => s.mapping_id === doctor.mapping_id)
          .map((s) => s.availability_id)
      ),
    [sessionsByAvailability, doctor.mapping_id]
  );

  const isTransferredIn = useCallback(
    (b: BookingV2) => !b.availability_id || !mySessionIds.has(b.availability_id),
    [mySessionIds]
  );

  const transferredIn = useMemo(
    () => bookings.filter(isTransferredIn).sort(byToken),
    [bookings, isTransferredIn]
  );

  const sessions = useMemo<SessionGroup[]>(() => {
    const own = bookings.filter((b) => !isTransferredIn(b));

    const ids = new Set<string>(mySessionIds);
    own.forEach((b) => b.availability_id && ids.add(b.availability_id));

    return Array.from(ids)
      .map((availabilityId) => ({
        availabilityId,
        meta: sessionsByAvailability[availabilityId],
        bookings: own
          .filter((b) => (b.availability_id ?? "") === availabilityId)
          .sort(byToken),
      }))
      .sort(
        (a, b) => timeValue(a.meta?.start_time) - timeValue(b.meta?.start_time)
      );
  }, [bookings, isTransferredIn, mySessionIds, sessionsByAvailability]);

  // Default to the session that's happening now (else the next upcoming, else
  // the first). Used only to seed the switcher.
  const defaultId = useMemo(() => {
    if (sessions.length === 0) return null;
    const now = moment().valueOf();
    const current = sessions.find((s) => {
      const start = timeValue(s.meta?.start_time);
      const end = s.meta?.end_time
        ? timeValue(s.meta.end_time)
        : Number.MAX_SAFE_INTEGER;
      return now >= start && now <= end;
    });
    if (current) return current.availabilityId;
    const upcoming = sessions.find(
      (s) => timeValue(s.meta?.end_time ?? s.meta?.start_time) >= now
    );
    return (upcoming ?? sessions[0]).availabilityId;
  }, [sessions]);

  const [activeId, setActiveId] = useState<string | null>(null);

  // Keep the active session valid as sessions arrive/change, without
  // overriding a manual switch.
  useEffect(() => {
    const ids = sessions.map((s) => s.availabilityId);
    if (!activeId || !ids.includes(activeId)) {
      setActiveId(defaultId);
    }
  }, [sessions, activeId, defaultId]);

  const active =
    sessions.find((s) => s.availabilityId === activeId) ?? sessions[0];
  const activeIndex = active
    ? sessions.findIndex((s) => s.availabilityId === active.availabilityId)
    : 0;

  // Own patients first, then transfers fill whatever budget is left, so the cap
  // applies to the merged list the viewer actually sees.
  const limit = tvConfig.patient_display_limit;
  const activeRows = active?.bookings ?? [];
  const shownActive = limit == null ? activeRows : activeRows.slice(0, limit);
  const shownTransferred =
    limit == null
      ? transferredIn
      : transferredIn.slice(0, Math.max(0, limit - shownActive.length));

  const paused =
    active &&
    (delaysByAvailability[active.availabilityId] ?? active.meta?.doc_delay)
      ?.is_paused
      ? delaysByAvailability[active.availabilityId] ?? active.meta?.doc_delay
      : undefined;

  // Session dropdown (replaces the tab row). Auto-selection of the current
  // session is handled by defaultId above — this only drives manual switching.
  const multiSession = sessions.length > 1;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  // Auto-scroll (v1 parity). Nobody can scroll a waiting-room TV, so when the
  // list is taller than its box the rows past the fold were simply never seen
  // — the "overflow" on long queues. Glide to the bottom, hold, glide back,
  // repeat. Re-measured on every leg so it follows rows arriving and leaving,
  // and idles (re-checking every few seconds) while the list fits. Restarts
  // from the top when the shown session changes.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const HOLD_MS = 2500;
    const RECHECK_MS = 4000;
    // ~40 CSS px/s at the 1080p design size; expressed in rem so it reads the
    // same on a scaled-down (or up) TV viewport.
    const rem =
      parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const pxPerSec = 2.5 * rem;

    let down = true;
    let timer: ReturnType<typeof setTimeout>;
    let raf = 0;
    let cancelled = false;

    const glide = (target: number, done: () => void) => {
      const start = el.scrollTop;
      const dist = target - start;
      const duration = Math.max(3000, (Math.abs(dist) / pxPerSec) * 1000);
      let t0: number | null = null;
      const step = (now: number) => {
        if (cancelled) return;
        if (t0 == null) t0 = now;
        const p = Math.min(1, (now - t0) / duration);
        const eased = p < 0.5 ? 2 * p * p : -1 + (4 - 2 * p) * p; // ease-in-out
        el.scrollTop = start + dist * eased;
        if (p < 1) raf = requestAnimationFrame(step);
        else done();
      };
      raf = requestAnimationFrame(step);
    };

    const cycle = () => {
      if (cancelled) return;
      const range = el.scrollHeight - el.clientHeight;
      if (range <= 1) {
        el.scrollTop = 0;
        down = true;
        timer = setTimeout(cycle, RECHECK_MS);
        return;
      }
      const target = down ? range : 0;
      down = !down;
      glide(target, () => {
        timer = setTimeout(cycle, HOLD_MS);
      });
    };

    el.scrollTop = 0;
    timer = setTimeout(cycle, HOLD_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [activeId]);

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl bg-white p-3 border-[1.5px] border-gray-300">
      {/* Active session — blue outline pill, top-right. Opens a dropdown of
          all sessions when the doctor has more than one (v1 behaviour). */}
      {active && (
        <div className="mb-2 flex shrink-0 justify-end">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => multiSession && setMenuOpen((o) => !o)}
              className={`flex items-center gap-1.5 rounded-lg border border-[#0F3CBE] bg-white px-3 py-0.5 font-medium text-[#0F3CBE] ${
                large ? "text-sm" : "text-xs"
              } ${multiSession ? "cursor-pointer" : "cursor-default"}`}
            >
              {rangeLabel(active.meta, activeIndex)}
              {multiSession && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`transition-transform ${
                    large ? "h-4 w-4" : "h-3.5 w-3.5"
                  } ${menuOpen ? "rotate-180" : ""}`}
                >
                  <path
                    d="M6 9l6 6 6-6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            {menuOpen && multiSession && (
              <div className="absolute right-0 z-20 mt-1 min-w-[11.25rem] overflow-hidden rounded-xl border border-[#E3E8F0] bg-white shadow-lg">
                {sessions.map((s, i) => {
                  const isSel = s.availabilityId === active?.availabilityId;
                  return (
                    <button
                      key={s.availabilityId}
                      type="button"
                      onClick={() => {
                        setActiveId(s.availabilityId);
                        setMenuOpen(false);
                      }}
                      className={`block w-full px-4 py-2.5 text-left ${
                        large ? "text-sm" : "text-xs"
                      } ${
                        isSel
                          ? "bg-[#EEF4FF] font-semibold text-[#0F3CBE]"
                          : "text-[#3C4658] hover:bg-[#F5F7FB]"
                      }`}
                    >
                      {rangeLabel(s.meta, i)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Doctor header — solid blue pill, white name + specialization. */}
      <div
        className={`flex shrink-0 items-center rounded-full bg-[#0F3CBE] ${
          compact ? "gap-2 px-2 py-1.5" : "gap-3 px-3 py-2"
        }`}
      >
        <Avatar doctor={doctor} size={large ? 56 : compact ? 40 : 48} />
        <div className="min-w-0 flex-1">
          <p
            className={`truncate font-bold leading-tight text-white ${
              large ? "text-2xl" : compact ? "text-base" : "text-lg"
            }`}
          >
            {formatDoctorName(doctor.full_name, doctor.show_name_prefix !== false)}
          </p>
          {doctor.specialization && (
            <p
              className={`truncate text-white/85 ${
                large ? "text-sm" : compact ? "text-[0.625rem]" : "text-xs"
              }`}
            >
              {doctor.specialization}
            </p>
          )}
        </div>
      </div>

      {/* Doctor-level room banner (single assigned room). */}
      {doctorRoom && (
        <div className="mt-2 shrink-0">
          <RoomBadge roomName={doctorRoom} large={large} />
        </div>
      )}

      {/* Paused banner — compact red pill, matching v1. */}
      {paused && (
        <div className="mt-2 flex shrink-0 items-center justify-center">
          <div
            className={`flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 px-3 py-1 text-white shadow-md ${
              large ? "text-sm" : "text-xs"
            }`}
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className={large ? "h-4 w-4 shrink-0" : "h-3.5 w-3.5 shrink-0"}
            >
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
            </svg>
            <span className="font-medium leading-snug">
              {paused.pause_reason || "Queue paused"}
            </span>
          </div>
        </div>
      )}

      {/* One merged list: own + transferred-in (the "T" tag marks transfers) */}
      {/*
        patient_display_limit caps the WHOLE list, not each half. The two arrays
        render as one visual queue, so budgeting across them is what makes "show 5"
        actually show five rows rather than five plus however many transfers exist.
        null means no limit, and the empty-state checks above deliberately still look
        at the full lists — a queue that has patients should never say "no patients"
        just because the limit is small.
      */}
      {/* scrollbar-hide: a scrollbar on a TV reads as "it doesn't fit" and ate
          row width; the auto-scroll above does the scrolling instead. */}
      <div
        ref={listRef}
        className="scrollbar-hide mt-2 min-h-0 flex-1 overflow-y-auto"
      >
        {!active && transferredIn.length === 0 ? (
          <div
            className={`flex items-center justify-center rounded-xl bg-[#F7F9FC] text-[#AEB7C7] ${
              large ? "h-28 text-lg" : "h-20 text-sm"
            }`}
          >
            No sessions today
          </div>
        ) : (active?.bookings.length ?? 0) + transferredIn.length === 0 ? (
          <div
            className={`flex items-center justify-center rounded-xl bg-[#F7F9FC] text-[#AEB7C7] ${
              large ? "h-28 text-lg" : "h-20 text-sm"
            }`}
          >
            No patients in queue
          </div>
        ) : (
          <div className={compact ? "space-y-1.5" : "space-y-2"}>
            {shownActive.map((b) => (
              <PatientRow
                key={b.booking_id}
                booking={b}
                name={formatName(b.full_name, b.prn_number)}
                density={density}
                showRoomTag={perPatientRoom}
              />
            ))}
            {shownTransferred.map((b) => (
              <PatientRow
                key={b.booking_id}
                booking={b}
                name={formatName(b.full_name, b.prn_number)}
                density={density}
                transferred
                showRoomTag={perPatientRoom}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorQueuePanel;
